import React from "react";
import { type FFPermissionMap } from "./PermissionMap";
import { getFormElementType } from "utilities/freefieldUtils";
import { Checkbox } from "components/Atoms/inputs/Checkbox";
import { routes } from "utilities/routing/generatedRoutes";

interface Props {
  category: server.dto.FreeFieldCategorySmall
  permissions: FFPermissionMap
  onPermissionChange: (ffIds: number[], newValue: server.dto.FreeFieldPermissionLevels) => void
  environment: server.dto.Environments
}

const FreefieldCategory = (props: Props) => {
  const { permissions } = props;

  const isViewChecked = (id: number) => {
    const perm = permissions[id];
    return perm != null && perm !== "hidden";
  };

  const isModifyChecked = (id: number) => permissions[id] === "viewModify";

  const onViewCheck = (id: number) => {
    if (permissions[id] === "hidden") {
      props.onPermissionChange([id], "view");
    } else {
      props.onPermissionChange([id], "hidden");
    }
  };

  const onModifyChecked = (id: number) => {
    if (permissions[id] === "viewModify") {
      props.onPermissionChange([id], "view");
    } else {
      props.onPermissionChange([id], "viewModify");
    }
  };

  const onAccessChecked = (id: number) => {
    if (permissions[id] === "viewModify") { props.onPermissionChange([id], "hidden"); } else { props.onPermissionChange([id], "viewModify"); }
  };

  const allViewCheckboxesChecked = props.category.definitions.length !== 0 && props.category.definitions.every(def => props.permissions[def.freeFieldDefinitionId] !== "hidden");

  const allModifyCheckboxesChecked = props.category.definitions.length !== 0 && props.category.definitions.every(def => props.permissions[def.freeFieldDefinitionId] === "viewModify");

  const onCategoryViewCheck = () => {
    if (!allViewCheckboxesChecked) {
      // Only change the 'hidden' ones. So not the the 'viewModify' ones
      // Otherwise viewModify ones get degraded to 'view' only
      const idsToChange = props.category.definitions
        .filter(def => props.permissions[def.freeFieldDefinitionId] === "hidden")
        .map(def => def.freeFieldDefinitionId);

      props.onPermissionChange(idsToChange, "view");
    } else {
      const allIds = props.category.definitions.map(def => def.freeFieldDefinitionId);
      props.onPermissionChange(allIds, "hidden");
    }
  };

  const onCategoryModifyCheck = () => {
    if (!allModifyCheckboxesChecked) {
      const allIds = props.category.definitions.map(def => def.freeFieldDefinitionId);
      props.onPermissionChange(allIds, "viewModify");
    } else {
      const allIds = props.category.definitions.map(def => def.freeFieldDefinitionId);
      props.onPermissionChange(allIds, "view");
    }
  };

  const onCategoryAccessCheck = () => {
    if (!allModifyCheckboxesChecked) {
      const allIds = props.category.definitions.map(def => def.freeFieldDefinitionId);
      props.onPermissionChange(allIds, "viewModify");
    } else {
      const allIds = props.category.definitions.map(def => def.freeFieldDefinitionId);
      props.onPermissionChange(allIds, "hidden");
    }
  };

  const smallColStyle = { flex: "0 0 100px" };

  return <div className="list-with-gaps line-layout">
    <div className="list-with-gaps-header">
      <div>
        <div className="grow1">{props.category.title}</div>
        {props.environment === "internal" &&
          <>
            <div style={smallColStyle}><Checkbox onChange={onCategoryViewCheck} checked={allViewCheckboxesChecked} label={RESX.GeneralLabels.Visible} /></div>
            <div style={smallColStyle}><Checkbox onChange={onCategoryModifyCheck} checked={allModifyCheckboxesChecked} label={RESX.SoUser.Modify} /></div>
          </>
        }
        {props.environment === "external" &&
          <>
            <div style={smallColStyle}><Checkbox onChange={onCategoryAccessCheck} checked={allModifyCheckboxesChecked} label={RESX.SoUser.Access} /></div>
          </>
        }
      </div>
    </div>

    <div className="list-with-gaps-body">
      {props.category.definitions.map(def => (
        <div key={def.freeFieldDefinitionId}>
          <div className="grow1">
            <h4><a href={routes.freeFieldDefinition_Detail(def.freeFieldDefinitionId).absolute}>{def.title}</a></h4>
            <div className="s1_gray">{getFormElementType(def.type).text}</div>

            {(def.hideFromItinerary || def.hideFromMobileApp) &&
              <div className="s1_small margin-top-small tag-list">
                {(def.hideFromItinerary) && <div className="s1_tag blue-bg">{RESX.FreeField.HiddenFromItinerary}</div>}
                {(def.hideFromMobileApp) && <div className="s1_tag small purple-bg">{RESX.FreeField.HiddenFromMobileApp}</div>}
              </div>}
          </div>

          {props.environment === "internal" && <>
            <div style={smallColStyle}>
              <Checkbox onChange={() => { onViewCheck(def.freeFieldDefinitionId); }} checked={isViewChecked(def.freeFieldDefinitionId)} />
            </div>
            <div style={smallColStyle}>
              <Checkbox onChange={() => { onModifyChecked(def.freeFieldDefinitionId); }} checked={isModifyChecked(def.freeFieldDefinitionId)} />
            </div>
          </>}
          {props.environment === "external" && <div style={smallColStyle}>
            <Checkbox onChange={() => { onAccessChecked(def.freeFieldDefinitionId); }} checked={isModifyChecked(def.freeFieldDefinitionId)} />
          </div>}
        </div>
      ))}
    </div>
  </div>;
};

export default FreefieldCategory;